import { useStore } from '@/store/index'
import { deepMerge } from '@/utils/tools'
import { useMemoizedFn } from 'ahooks'
import { observer } from 'mobx-react-lite'
import { FC, cloneElement } from 'react'

export interface BaseRiseColorPoops {
  value?: number | string
  market_rise_color: 'green' | 'red'
  children: any
}

/**
 * 获取当前涨跌色值
 * @returns {{ color: styleColor }}
 */
export const marketRiseStyleColor = ({
  value = 1,
  market_rise_color,
}: Omit<BaseRiseColorPoops, 'children' | 'value'> & { value: number }) => {
  const styleColor = () => {
    if (value >= 0) {
      return market_rise_color === 'green' ? 'var(--color-pd-2)' : 'var(--color-pd-1)'
    } else {
      return market_rise_color === 'green' ? 'var(--color-pd-1)' : 'var(--color-pd-2)'
    }
  }

  const themeColor = () => {
    if (value >= 0) {
      return market_rise_color === 'green' ? '--color-pd-2' : '--color-pd-1'
    } else {
      return market_rise_color === 'green' ? '--color-pd-1' : '--color-pd-2'
    }
  }

  return { color: styleColor(), value: themeColor() }
}

export const useRiseColor = () => {
  const { riseColorValue = 'red' } = useStore().globalStore

  const getRiseColor = useMemoizedFn((value: number) => {
    return {
      ...marketRiseStyleColor({ value: value, market_rise_color: riseColorValue }),
    }
  })

  return { getRiseColor }
}

const BaseRiseColor: FC<Pick<BaseRiseColorPoops, 'children' | 'value'> & { className?: string }> = ({
  children,
  value,
}) => {
  const { riseColorValue } = useStore().globalStore

  if (!value || !Number(value)) {
    return children
  }

  if (!children) {
    return marketRiseStyleColor({ value: Number(value), market_rise_color: riseColorValue })
  }

  if (typeof children?.map !== 'function') {
    return cloneElement(children, {
      style: deepMerge(
        children.props?.style ?? {},
        marketRiseStyleColor({ value: Number(value), market_rise_color: riseColorValue })
      ),
    })
  }

  return children?.map((child: any) => {
    if (typeof child === 'object' && child !== null) {
      return cloneElement(child, {
        style: deepMerge(
          child.props.style ?? {},
          marketRiseStyleColor({ value: Number(value), market_rise_color: riseColorValue })
        ),
      })
    }
    return child
  })
}

export default observer(BaseRiseColor)
